import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import type { Op } from 'quill/core';
import { Delta } from 'quill/core';
import React, { useRef, useState } from 'react';
import * as styles from './Comment.module.scss';
import { OPTIMISTIC_COMMENT_IDS } from './CommentCreate';
import CommentInput from './CommentInput';
import CommentMenu from './CommentMenu';
import CommentPreview from './CommentPreview';
import CommentReactionList from './CommentReactionList';
import CommentTimestamp from './CommentTimestamp';
import FoldedComments from './FoldedComments';
import MagneticWallItemOverflowMenu, {
  MagneticWallOverflowButton,
} from './MagneticWallItemOverflowMenu';
import showFullPageSheet from './showFullPageSheet';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import EmojiPicker, { EmojiPickerPopOver } from '../../components/EmojiPicker';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import OpacityMotionTransition from '../../components/OpacityMotionTransition';
import Tooltip from '../../components/Tooltip';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import checkIcon from '../../icons/non-prefilled/16x16/check.svg';
import editIcon from '../../icons/non-prefilled/16x16/editPost.svg';
import linkIcon from '../../icons/non-prefilled/16x16/link.svg';
import reactionIcon from '../../icons/non-prefilled/16x16/reaction.svg';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import { UIActions } from '../../ui/uiSlice';
import addTestId from '../../utils/addTestId';
import getFirstName from '../../utils/text/getFirstName';
import commentThreadHash from '../../utils/url/commentThreadHash';
import fullOrgUrl from '../../utils/url/fullOrgUrl';
import postPath from '../../utils/url/postPath';
import MagneticWallItemAvatar from '../MagneticWallItemAvatar';
import { useOptimisticItemContentRects } from '../useOptimisticItemContentRects';
import useShareSecret from '../useShareSecret';
import isContentEmpty from '../utils/isContentEmpty';

const COMMENT_META_MARGIN_BOTTOM = parseInt(styles.commentMetaMarginBottom, 10);

const THREAD_FRAGMENT = gql(`
  fragment Comment_thread on RootThreadFields {
    id
  }
`);

const POST_FRAGMENT = gql(`
  fragment Comment_post on RootPostFields {
    id
    ...postPath_post
  }
`);

const MAX_LINE_COUNT = 3;
const LARGE_COMMENT_LENGTH_THRESHOLD = 400;

const COMMENT_FRAGMENT = gql(`
  fragment Comment_comment on CommentFields {
    id
    content
    author {
      id
      ...MagneticWallItemAvatar_user
    }
    reactions {
      ...CommentReactionList_reactions
    }
    insertedAt
    updatedAt
  }
`);

export const COMMENT_ANIMATION_DURATION = 0.2;

const UPDATE_COMMENT_MUTATION = gql(/** @complexity 2 */ `
  mutation updateComment($id: ID!, $content: String!) {
    updateComment(commentId: $id, content: $content) {
      id
      content
      updatedAt
    }
  }
`);

const useOptimisticUpdateComment = (
  comment: DocumentType<typeof COMMENT_FRAGMENT>,
) => {
  return useMutation(UPDATE_COMMENT_MUTATION, {
    optimisticResponse: (vars) => ({
      __typename: 'RootMutationType' as const,
      updateComment: {
        ...comment,
        content: JSON.parse(vars.content),
        updatedAt: new Date().toISOString(),
      },
    }),
  });
};

export const CommentContainer = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  return (
    <div
      ref={ref}
      {...props}
      className={classNames('comment', styles.comment, props.className)}
    />
  );
});

export const CommentLeft = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  return (
    <div
      ref={ref}
      {...props}
      className={classNames(styles.commentLeft, props.className)}
    />
  );
});

export const CommentRight = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  return (
    <div
      ref={ref}
      {...props}
      className={classNames(
        'comment-right',
        styles.commentRight,
        props.className,
      )}
    />
  );
});

export const CommentContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  return (
    <div
      ref={ref}
      {...props}
      className={classNames(
        'comment-content',
        styles.commentContent,
        props.className,
      )}
    />
  );
});

interface CommentMetaProps {
  children: React.ReactNode;
}

export const CommentMeta = ({ children }: CommentMetaProps) => (
  <div className={styles.commentMeta}>{children}</div>
);

interface CommentAuthorProps {
  children: React.ReactNode;
}

export const CommentAuthor = ({ children }: CommentAuthorProps) => (
  <span className={styles.commentAuthor}>{children}</span>
);

export const CommentActions = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  return (
    <div
      ref={ref}
      {...props}
      className={classNames(styles.commentActions, props.className)}
    />
  );
});

interface CommentProps {
  comment: DocumentType<typeof COMMENT_FRAGMENT>;
  post: DocumentType<typeof POST_FRAGMENT>;
  thread: DocumentType<typeof THREAD_FRAGMENT>;
  onResize: (avoidRepositioningFocusedThread?: boolean) => void;
  onResolve: () => void;
  first: boolean;
  last: boolean;
  expanded: boolean;
  initialShowingMore?: boolean;
  editing: boolean;
  hasExtraBottomPadding?: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  shouldHideToolbar?: (offsetTop: number) => boolean;
  disabledAnimatedPreview?: boolean;
  threadHovered?: boolean;
  deleteThread: () => void;
  deleteComment: () => void;
  toggleCommentReaction: React.ComponentProps<
    typeof CommentReactionList
  >['toggleCommentReaction'];
}

const Comment = ({
  comment,
  onResize,
  onResolve,
  thread,
  post,
  first,
  last,
  expanded,
  editing,
  onEdit,
  onCancelEdit,
  threadHovered,
  deleteThread,
  deleteComment,
  toggleCommentReaction,
  hasExtraBottomPadding,
}: CommentProps) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const shareSecret = useShareSecret();

  const [updateComment] = useOptimisticUpdateComment(comment);

  const copyThreadLink = () => {
    const url = fullOrgUrl(
      postPath(post, { shareSecret, anchor: commentThreadHash(thread.id) }),
    );

    navigator.clipboard.writeText(url).then(() => {
      dispatch(
        UIActions.pushToast({
          level: 'success',
          message: 'Link copied to clipboard',
        }),
      );
    });
  };

  const [isHovered, setIsHovered] = useState(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [editContent, setEditContent] = useState<Op[] | null>(null);
  const commentContentRef = useRef<HTMLDivElement>(null);

  const currentUser = useAppSelector((state) => state.session.user);
  const isEditable = useAppSelector((state) => state.editor.editable);
  const isAuthor = comment.author.id === currentUser.id;
  const canResolve = isEditable || isAuthor;
  const isFullPageSheet = showFullPageSheet();

  if (editing && !editContent) {
    setEditContent(comment.content);
  }

  const onEditChange = (newContent: Op[]) => {
    setEditContent(newContent);
    onResize(true);
  };

  const onEmojiPickerOpenChange = (open: boolean) => {
    setIsEmojiPickerOpen(open);
  };

  const onSave = () => {
    if (!editContent?.length) {
      return;
    }

    try {
      updateComment({
        variables: { id: comment.id, content: JSON.stringify(editContent) },
      });
      onCancelEdit();
    } catch {
      dispatch(
        UIActions.pushToast({
          level: 'error',
          message:
            'Oops, we could not save the comment. Please try again or contact support.',
        }),
      );
    }
  };

  const getCommentMenuItems = (includingReactionItem: boolean) => {
    const menuItems = [];

    if (first && canResolve && isFullPageSheet) {
      menuItems.push(
        <MenuItem key="resolve" icon={checkIcon} onClick={onResolve}>
          Resolve
        </MenuItem>,
      );
    }

    if (isAuthor) {
      menuItems.push(
        <MenuItem key="edit" icon={editIcon} onClick={onEdit}>
          Edit comment
        </MenuItem>,
      );
    }

    if (!isFullPageSheet || first) {
      menuItems.push(
        <MenuItem key="copy" icon={linkIcon} onClick={copyThreadLink}>
          Copy link to thread
        </MenuItem>,
      );
    }

    if (includingReactionItem) {
      menuItems.push(
        <MenuItem
          key="reaction"
          icon={reactionIcon}
          onClick={() => onEmojiPickerOpenChange(true)}
        >
          Add reaction
        </MenuItem>,
      );
    }

    if (isAuthor) {
      if (menuItems.length > 0) {
        menuItems.push(<MenuSeparator key="separator" />);
      }
      if (first) {
        menuItems.push(
          <MenuItem
            icon={trashIcon}
            key="delete_thread"
            danger
            onClick={deleteThread}
          >
            Delete thread
          </MenuItem>,
        );
      } else {
        menuItems.push(
          <MenuItem
            icon={trashIcon}
            key="delete"
            danger
            onClick={deleteComment}
          >
            Delete comment
          </MenuItem>,
        );
      }
    }

    return menuItems;
  };

  const commonButtonProps = {
    emojiPickerOpen: isEmojiPickerOpen,
    onEmojiPickerOpenChange,
    toggleCommentReaction,
  };
  const optimisticItemContentRects = useOptimisticItemContentRects();

  const isToolbarEnabled =
    !editing &&
    // When the thread was optimistically added, the comments of the thread have randomly
    // generated ids that can't be acted upon yet, so we need to wait for the real comments.
    // Until then, we can just hide the toolbar for these comments.
    !optimisticItemContentRects.has(thread.id) &&
    // Similar to above, while the comment is optimistic, we don't want to show the toolbar
    !OPTIMISTIC_COMMENT_IDS.has(comment.id) &&
    ((expanded && (isFullPageSheet || isHovered)) || (first && threadHovered));

  return (
    <CommentContainer
      ref={elementRef}
      className={classNames({
        'comment-large':
          expanded &&
          new Delta(comment.content).length() > LARGE_COMMENT_LENGTH_THRESHOLD,
        [styles.hasExtraBottomPadding]: hasExtraBottomPadding,
        [styles.editing]: editing,
        [styles.first]: first,
        [styles.last]: last,
      })}
      {...addTestId(`comment-${comment.id}`)}
    >
      <CommentLeft>
        <FoldedComments.LineContainer>
          <MagneticWallItemAvatar user={comment.author} />
        </FoldedComments.LineContainer>
      </CommentLeft>
      <CommentRight
        className={classNames({
          [styles.commentRightSheet]: isFullPageSheet,
        })}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <AnimatePresence initial={false}>
          {expanded && !editing && (
            <OpacityMotionTransition
              key="comment-meta"
              animate={{
                height: 'auto',
                marginBottom: COMMENT_META_MARGIN_BOTTOM,
              }}
              initial={{ height: 0, marginBottom: COMMENT_META_MARGIN_BOTTOM }}
              exit={{ height: 0, marginBottom: 0 }}
              className={styles.commentMeta}
              transition={{ duration: COMMENT_ANIMATION_DURATION }}
              onAnimationComplete={() => onResize()}
            >
              <CommentAuthor>{getFirstName(comment.author.name)}</CommentAuthor>
              <CommentTimestamp
                insertedAt={comment.insertedAt}
                updatedAt={comment.updatedAt}
                onClick={first ? copyThreadLink : undefined}
              />
            </OpacityMotionTransition>
          )}
        </AnimatePresence>
        <CommentContent
          ref={commentContentRef}
          {...addTestId('commentContent')}
        >
          {editing && editContent ? (
            <CommentInput
              autoFocus
              initialContent={editContent}
              onChange={onEditChange}
              onSave={onSave}
            />
          ) : (
            <CommentPreview
              initialHeight={commentContentRef.current?.clientHeight}
              collapseLines={!expanded}
              content={comment.content}
              link={expanded}
              showHoverCard={expanded}
              maxLines={MAX_LINE_COUNT}
              disableLineClamp={expanded}
              onResize={onResize}
            />
          )}
        </CommentContent>
        <AnimatePresence>
          {isToolbarEnabled && (
            <OpacityMotionTransition
              key="comment-button"
              className={styles.commentButton}
              transition={{ duration: COMMENT_ANIMATION_DURATION }}
            >
              {isFullPageSheet ? (
                <CommentMenu
                  menuItems={getCommentMenuItems(true)}
                  {...commonButtonProps}
                />
              ) : (
                <MagneticWallItemOverflowMenu
                  menuItems={getCommentMenuItems(false)}
                >
                  {first && canResolve && (
                    <Tooltip content="Resolve thread">
                      <MagneticWallOverflowButton
                        onClick={onResolve}
                        icon={checkIcon}
                        ariaLabel="Resolve thread"
                      />
                    </Tooltip>
                  )}
                  <EmojiPickerPopOver
                    open={isEmojiPickerOpen}
                    placement="bottom-end"
                    setOpen={onEmojiPickerOpenChange}
                    offset="small"
                    target={
                      <MagneticWallOverflowButton
                        icon={reactionIcon}
                        active={isEmojiPickerOpen}
                        onClick={() =>
                          onEmojiPickerOpenChange(!isEmojiPickerOpen)
                        }
                      />
                    }
                  >
                    <EmojiPicker
                      onPick={(emoji) => {
                        onEmojiPickerOpenChange(false);
                        toggleCommentReaction(emoji);
                      }}
                    />
                  </EmojiPickerPopOver>
                </MagneticWallItemOverflowMenu>
              )}
            </OpacityMotionTransition>
          )}
          {expanded && editing && (
            <OpacityMotionTransition
              key="comment-actions"
              className={styles.commentActions}
              transition={{ duration: COMMENT_ANIMATION_DURATION }}
              animate={{ height: 'auto', marginTop: 12 }}
              initial={{ height: 0, marginTop: 0 }}
              exit={{ height: 0, marginTop: 0 }}
            >
              <div className={styles.editingActions}>
                <Button variant="secondary" onClick={onCancelEdit} size="small">
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  disabled={isContentEmpty(editContent)}
                  onClick={onSave}
                  size="small"
                >
                  Save
                </Button>
              </div>
            </OpacityMotionTransition>
          )}
          {expanded &&
            !editing &&
            comment.reactions &&
            comment.reactions.length > 0 && (
              <OpacityMotionTransition
                key="comment-reactions"
                className={styles.commentReactions}
                transition={{ duration: COMMENT_ANIMATION_DURATION }}
                animate={{ height: 'auto' }}
                initial={{ height: 0 }}
                exit={{ height: 0 }}
                onAnimationComplete={() => onResize()}
              >
                <CommentReactionList
                  reactions={comment.reactions}
                  toggleCommentReaction={toggleCommentReaction}
                />
              </OpacityMotionTransition>
            )}
        </AnimatePresence>
      </CommentRight>
    </CommentContainer>
  );
};

export default Comment;
